import { useDb } from '@/lib/dbContext'
import type { Callout, Job, ServiceStatus } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { ServicesScreen, type ServiceItem } from '@/screens/ServicesScreen'
import { isArchivedService, useServiceItems } from '@/screens/useServiceItems'

/**
 * The services-page mutation handlers (note, photo, status, assign), shared
 * with other hosts of the services ledger (e.g. the kiosk-control check-out
 * selector).
 */
export function useServiceMutations() {
  const db = useDb()

  const handleAddNote = async (item: ServiceItem, text: string) =>
    withSpan(
      {
        op: 'service.note.add',
        name: 'Add note from services page',
        attributes: { serviceType: item.type, serviceId: item.privateId },
      },
      async () => {
        const input = { text }
        const { transaction } =
          item.type === 'job'
            ? db.appendJobLogWithTransaction(item.privateId, input)
            : item.type === 'callout'
              ? db.appendLogWithTransaction(item.privateId, input)
              : db.appendSessionLogWithTransaction(item.privateId, input)
        await transaction.isPersisted.promise
      },
    )

  const handleAddPhoto = async (
    item: ServiceItem,
    input: { photoFiles: File[]; caption: string; photoUrls: string[] },
  ) =>
    withSpan(
      {
        op: 'service.note.add_photo',
        name: 'Add photo from services page',
        attributes: { serviceType: item.type, serviceId: item.privateId },
      },
      async () => {
        const logInput = {
          caption: input.caption,
          photoFiles: input.photoFiles,
          photoUrl: input.photoUrls[0],
          photoUrls: input.photoUrls,
        }
        const { transaction } =
          item.type === 'job'
            ? db.appendJobLogWithTransaction(item.privateId, logInput)
            : item.type === 'callout'
              ? db.appendLogWithTransaction(item.privateId, logInput)
              : db.appendSessionLogWithTransaction(item.privateId, logInput)
        await transaction.isPersisted.promise
      },
    )

  const handleEditNote = async (
    item: ServiceItem,
    noteId: string,
    patch: { text?: string; caption?: string },
  ) =>
    withSpan(
      {
        op: 'service.note.edit',
        name: 'Edit note from services page',
        attributes: { serviceType: item.type, serviceId: item.privateId },
      },
      async () => {
        if (item.type === 'job') {
          db.updateJobLog(item.privateId, noteId, patch)
        } else if (item.type === 'callout') {
          db.updateLog(item.privateId, noteId, patch)
        } else {
          db.updateSessionLog(item.privateId, noteId, patch)
        }
      },
    )

  const handleDeleteNote = async (item: ServiceItem, noteId: string) =>
    withSpan(
      {
        op: 'service.note.delete',
        name: 'Delete note from services page',
        attributes: { serviceType: item.type, serviceId: item.privateId },
      },
      async () => {
        if (item.type === 'job') {
          db.deleteJobLog(item.privateId, noteId)
        } else if (item.type === 'callout') {
          db.deleteLog(item.privateId, noteId)
        } else {
          db.deleteSessionLog(item.privateId, noteId)
        }
      },
    )

  const handleChangeStatus = async (
    item: ServiceItem,
    status: ServiceStatus,
    reason: string,
    occurredAt?: string,
  ) =>
    withSpan(
      {
        op: `${item.type === 'job' ? 'job' : item.type === 'callout' ? 'callout' : 'remote-session'}.status.change`,
        name: 'Change status (services page)',
        attributes: { serviceId: item.privateId, nextStatus: status },
      },
      async () => {
        const statusReason = reason || undefined
        if (item.type === 'callout') {
          await db.updateCalloutAndWait(item.privateId, {
            status: status as Callout['status'],
            statusReason,
            statusOccurredAt: occurredAt,
          })
          return
        }
        if (item.type === 'job') {
          await db.updateJobAndWait(item.privateId, {
            status: status as Job['status'],
            statusReason,
            statusOccurredAt: occurredAt,
          })
          return
        }
        await db.updateSessionAndWait(item.privateId, {
          status: status as Callout['status'],
          statusReason,
          statusOccurredAt: occurredAt,
        })
      },
    )

  const handleAssign = async (item: ServiceItem, userId: string) =>
    withSpan(
      {
        op: `${item.type === 'job' ? 'job' : item.type === 'callout' ? 'callout' : 'remote-session'}.assign`,
        name: 'Assign technician from services page',
        attributes: { serviceId: item.privateId, userId },
      },
      () =>
        item.type === 'job'
          ? db.assignJobAndWait(item.privateId, userId)
          : item.type === 'callout'
            ? db.assignCalloutAndWait(item.privateId, userId)
            : db.assignSessionAndWait(item.privateId, userId),
    )

  return {
    onAddNote: handleAddNote,
    onAddPhoto: handleAddPhoto,
    onEditNote: handleEditNote,
    onDeleteNote: handleDeleteNote,
    onChangeStatus: handleChangeStatus,
    onAssign: handleAssign,
    getAuthorDisplayName: db.getAuthorDisplayName,
  }
}

export function ServicesScreenContainer() {
  const { isLoading, isError, services, users } = useServiceItems()
  const mutations = useServiceMutations()

  if (isLoading || isError) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-container mx-auto space-y-2 px-4 py-6">
          <h1 className="text-2xl font-semibold">Services</h1>
          <p className="text-sm text-slate-600">
            {isLoading ? 'Loading services…' : 'Unable to load services right now.'}
          </p>
        </main>
      </div>
    )
  }

  const activeServices = services.filter((item) => !isArchivedService(item))

  return <ServicesScreen services={activeServices} users={users} {...mutations} />
}
